import { PageHeader, DataTable, LoadingState, FilterBar, ActionToolbar, EmptyState } from '../components/AdminPageUI';
import SummaryStrip, { SummaryMetric } from '../components/SummaryStrip';
import StudentAvatar from '../components/StudentAvatar';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Grid, Button, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, IconButton, Card, CardContent,
  Container, Avatar, Stack, Tooltip, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, Alert,
  Collapse, FormControlLabel, Checkbox, Divider, Switch, useMediaQuery, alpha, useTheme, Paper
} from '@mui/material';
import {
  Add, Edit, Delete, Print, Payment, CloudUpload, Preview,
  ExpandMore, ExpandLess, Receipt, Description, Close,
  AttachMoney, School, CalendarToday
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { pdf } from '@react-pdf/renderer';
import api from '../utils/Api';
import ReceiptPDF from '../components/ReceiptPDF';
import FeeStatementPDF from '../components/FeeStatementPDF';
import QRCode from 'qrcode';

// Helper functions outside component (keep the same)
const convertMongoNumber = (value) => {
  if (value == null) return 0;
  if (typeof value === 'object') {
    if (value.$numberInt) return parseInt(value.$numberInt);
    if (value.$numberDouble) return parseFloat(value.$numberDouble);
    if (value.$numberDecimal) return parseFloat(value.$numberDecimal);
    return 0;
  }
  return typeof value === 'number' ? value : 0;
};

const transformBill = (bill) => {
  if (!bill) return null;

  const studentInfo = bill.student?.name
    ? { ...bill.student }
    : { _id: bill.student, name: 'N/A' };

  const classInfo = bill.class?.name
    ? {
      _id: bill.class._id,
      name: bill.class.name,
      displayName: bill.class.displayName
    }
    : { _id: bill.class, name: 'N/A' };

  return {
    ...bill,
    items: bill.items?.map(item => ({
      ...item,
      name: item.name,
      amount: convertMongoNumber(item.amount),
      paid: convertMongoNumber(item.paid),
      balance: convertMongoNumber(item.balance),
      isVariable: item.isVariable || false
    })) || [],
    totalAmount: convertMongoNumber(bill.totalAmount),
    totalPaid: convertMongoNumber(bill.totalPaid),
    balance: convertMongoNumber(bill.balance), // Ensure balance is also converted
    billingMode: bill.billingMode || bill.termFeeBillingMode || 'fixed',
    termFeeBillingMode: bill.termFeeBillingMode || bill.billingMode || 'fixed',
    dailyFeeLabel: bill.dailyFeeLabel || 'Daily School Fees',
    isDailyVariable: bill.isDailyVariable || bill.billingMode === 'daily-variable' || bill.termFeeBillingMode === 'daily-variable',
    status: bill.status || 'Unpaid',
    student: studentInfo,
    class: classInfo,
    isManualUpdate: bill.isManualUpdate || false
  };
};

const isDailyVariableBill = (bill) =>
  bill?.billingMode === 'daily-variable' ||
  bill?.termFeeBillingMode === 'daily-variable' ||
  bill?.isDailyVariable === true;

const TermBillingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // State management (keep all existing state)
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [students, setStudents] = useState([]);
  const [feeTemplates, setFeeTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [termBills, setTermBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [existingBill, setExistingBill] = useState(null);
  const [billDialog, setBillDialog] = useState({
    open: false, mode: 'edit', bill: null, studentId: '',
    items: [{ name: '', amount: '', paid: 0, balance: 0 }], paymentAmount: 0, paymentNote: ''
  });
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newItemName, setNewItemName] = useState('');
  const [newItemAmount, setNewItemAmount] = useState('');
  const [newItemMandatory, setNewItemMandatory] = useState(true);
  const [templateItems, setTemplateItems] = useState([]);
  const [bulkItemsInput, setBulkItemsInput] = useState('');
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [availableAcademicYears, setAvailableAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState('');
  const userSelectedAcademicYearRef = useRef(false);
  const [availableTerms, setAvailableTerms] = useState([]);
  const [term, setTerm] = useState('');
  const [hasGenerated, setHasGenerated] = useState(false);

  // Memoized and sorted term bills
  const sortedTermBills = React.useMemo(() => {
    return [...termBills].sort((a, b) => {
      const nameA = (a.student?.name || '').toUpperCase();
      const nameB = (b.student?.name || '').toUpperCase();
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });
  }, [termBills]);

  // PDF generation states
  const [generatingFeeStatement, setGeneratingFeeStatement] = useState(null);
  const [generatingReceipt, setGeneratingReceipt] = useState(null);

  // Reverse payment confirmation dialog state
  const [reverseDialog, setReverseDialog] = useState({ open: false, payment: null, bill: null, loading: false });

  // State for schoolId (fetched from API instead of localStorage)
  const [schoolId, setSchoolId] = useState(null);

  const [schoolWideSummary, setSchoolWideSummary] = useState(null);

  const fetchSchoolWideSummary = useCallback(async () => {
    if (!term || !academicYear) return;
    try {
      const res = await api.get('/fees/school-wide-summary', { params: { term, academicYear } });
      if (res.data?.success) {
        setSchoolWideSummary(res.data.summary);
      }
    } catch (err) {
      console.error('Failed to fetch school-wide summary:', err);
    }
  }, [term, academicYear]);

  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [auditReportData, setAuditReportData] = useState(null);
  const [isFetchingAudit, setIsFetchingAudit] = useState(false);
  const [auditReportDateFilter, setAuditReportDateFilter] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  // Data fetching functions (keep all existing functions)
  const fetchClasses = useCallback(async () => {
    try {
      const res = await api.get('/classes');
      const classList = Array.isArray(res.data) ? res.data :
        res.data.classes ? res.data.classes : [];
      setClasses(classList);
      if (classList.length > 0) setSelectedClassId(classList[0]._id);
    } catch (err) {
      console.error('Failed to load classes:', err);
      toast.error('Failed to load classes');
    }
  }, []);

  const fetchTerms = useCallback(async () => {
    if (!schoolId) {
      setAvailableTerms([]);
      setTerm('');
      return;
    }
    try {
      const res = await api.get('/term/all', { params: { school: schoolId } });
      const termsData = res.data?.allTerms || [];
      const currentActiveTerm = termsData.find(t => t.isCurrent);

      let targetYear = academicYear;
      if (!academicYear && currentActiveTerm) {
        if (userSelectedAcademicYearRef.current) return;
        targetYear = currentActiveTerm.academicYear;
        setAcademicYear(current => current || targetYear);
      }

      const filteredTerms = targetYear ? termsData.filter(t => t.academicYear === targetYear) : termsData;
      const uniqueTerms = [...new Set(filteredTerms.map(t => t.term))].sort();
      setAvailableTerms(uniqueTerms);

      if (filteredTerms.length > 0) {
        const targetCurrentTerm = filteredTerms.find(t => t.isCurrent) || filteredTerms[0];
        if (!term) {
          setTerm(targetCurrentTerm.term);
        } else if (!uniqueTerms.includes(term)) {
          setTerm(targetCurrentTerm.term);
        }
      } else {
        setAvailableTerms([]);
        setTerm('');
      }
    } catch (err) {
      console.error('Failed to fetch terms:', err);
      toast.error('Unable to load terms');
    }
  }, [schoolId, academicYear, term]);

  const fetchAcademicYears = useCallback(async () => {
    if (!schoolId) return;
    try {
      const res = await api.get('/term/years', { params: { school: schoolId } });
      const years = res.data?.academicYears || [];
      if (years.length > 0) {
        const sortedYears = years.sort((a, b) => b.localeCompare(a));
        setAvailableAcademicYears(sortedYears);
        setAcademicYear(current => current || sortedYears[0]);
      }
    } catch (err) {
      console.error('Failed to fetch academic years:', err);
      toast.error('Unable to load academic years');
    }
  }, [schoolId]);

  const fetchFeeTemplates = useCallback(async () => {
    try {
      if (!schoolId) return;
      const res = await api.get('/fees/fee-templates', { params: { school: schoolId } });
      const templates = Array.isArray(res.data) ? res.data : res.data.templates ? res.data.templates : [];
      setFeeTemplates(templates);
      if (templates.length > 0) setSelectedTemplateId(templates[0]._id);
    } catch (err) {
      console.error('Failed to fetch fee templates:', err);
      toast.error('Failed to load fee templates');
    }
  }, [schoolId]);

  const fetchSchoolInfo = useCallback(async () => {
    try {
      const res = await api.get('/school-info');
      setSchoolInfo(res.data);
      // Set schoolId from the fetched info
      if (res.data?.school?._id) {
        setSchoolId(res.data.school._id);
      } else if (res.data?.school) {
        setSchoolId(res.data.school);
      }
    } catch (error) {
      console.error('Error fetching school info:', error);
      toast.error('Failed to load school information');
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchSchoolInfo();
    fetchClasses();
  }, [fetchSchoolInfo, fetchClasses]);

  const fetchStudents = useCallback(async () => {
    if (!selectedClassId) return;
    try {
      const res = await api.get('/students', { params: { classId: selectedClassId } });
      const formattedStudents = res.data.map(student => ({
        ...student,
        user: student.user || { name: student.name, email: student.email },
        class: classes.find(c => c._id === (student.class?._id || student.classId || student.class)) || student.class || {}
      }));
      setStudents(formattedStudents);
    } catch (err) {
      console.error('Error fetching students:', err);
      toast.error('Failed to load student data');
    }
  }, [selectedClassId, classes]);

  const fetchTermBills = useCallback(async () => {
    if (!selectedClassId || !term || !academicYear) {
      setTermBills([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get('/fees/term-bills', { params: { classId: selectedClassId, term, academicYear } });
      const transformedBills = (res.data?.data || []).map(transformBill);
      setTermBills(transformedBills);
    } catch (err) {
      console.error('Error fetching term bills:', err);
      toast.error('Failed to load billing data');
      setTermBills([]);
    } finally {
      setLoading(false);
    }
  }, [selectedClassId, term, academicYear]);

  const fetchAuditReport = useCallback(async () => {
    if (!schoolId || !term || !academicYear) return;
    setIsFetchingAudit(true);
    try {
      const res = await api.get('/fees/audit-report', {
        params: {
          date: auditReportDateFilter,
          term,
          academicYear
        }
      });
      if (res.data?.success) {
        setAuditReportData(res.data);
      } else {
        setAuditReportData(null);
      }
    } catch (err) {
      console.error('Failed to fetch audit report:', err);
      toast.error('Failed to load audit report');
      setAuditReportData(null);
    } finally {
      setIsFetchingAudit(false);
    }
  }, [schoolId, term, academicYear, auditReportDateFilter]);

  useEffect(() => {
    if (showAuditDialog) {
      fetchAuditReport();
    }
  }, [showAuditDialog, auditReportDateFilter, fetchAuditReport]);

  // Handler functions (keep all existing handlers)
  const handleFeeStatementClick = async (bill) => {
    setGeneratingFeeStatement(bill._id);
    try {
      const student = students.find(s => s._id === bill.student?._id) || bill.student || {};

      // Fetch images using proxy to avoid CORS
      const [logoBase64, signatureBase64] = await Promise.all([
        fetchImageAsBase64(schoolInfo?.logo),
        fetchImageAsBase64(schoolInfo?.headteacherSignature || schoolInfo?.headTeacherSignature)
      ]);

      // Generate verifiable QR code
      const qrData = JSON.stringify({
        doc: 'Fee Statement',
        stu: student?.name,
        cls: bill.class?.displayName || bill.class?.name || student?.class?.name,
        term: `${bill.term}, ${bill.academicYear}`,
        tot: bill.totalAmount,
        pd: bill.totalPaid,
        id: String(bill._id).substr(-6)
      });
      const qrBase64 = await QRCode.toDataURL(qrData, {
        width: 150,
        margin: 1,
        color: { dark: '#000000', light: '#ffffff' }
      });

      const blob = await pdf(
        <FeeStatementPDF
          student={student}
          bill={bill}
          schoolInfo={schoolInfo}
          logoBase64={logoBase64}
          signatureBase64={signatureBase64}
          qrBase64={qrBase64}
        />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fee_statement_${student?.name?.replace(/\s+/g, '_') || 'student'}_${bill.term || 'term'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate fee statement');
    } finally {
      setGeneratingFeeStatement(null);
    }
  };

  // Helper to fetch image via proxy and convert to Base64
  // Helper to fetch image via proxy and convert to JPEG Base64 (to avoid PDF Buffer issues with PNGs)
  const fetchImageAsBase64 = async (url) => {
    if (!url) return null;
    // If it's already data URI, we might want to convert it too if it's PNG, but let's assume raw data URIs are fine or user provided.
    // Actually, if it's a PNG data URI, it might still crash. But let's handle the fetched ones first.
    if (url.startsWith('data:')) return url;

    try {
      // Use our backend proxy to avoid CORS
      const response = await api.get('/school-info/proxy-image', {
        params: { url },
        responseType: 'blob'
      });

      const blob = response.data;
      const blobUrl = URL.createObjectURL(blob);

      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous'; // Just in case, though blob is local
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');

            // Fill white background to handle transparency since JPEG doesn't support it
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            ctx.drawImage(img, 0, 0);

            // Convert to JPEG avoids 'Buffer is not defined' in some PDF renderers for PNG
            const jpegData = canvas.toDataURL('image/jpeg', 0.9);
            URL.revokeObjectURL(blobUrl);
            resolve(jpegData);
          } catch (e) {
            URL.revokeObjectURL(blobUrl);
            reject(e);
          }
        };
        img.onerror = (e) => {
          URL.revokeObjectURL(blobUrl);
          reject(e);
        };
        img.src = blobUrl;
      });
    } catch (error) {
      console.warn('Failed to fetch/convert image:', error);
      return null;
    }
  };

  const handlePrintReceiptClick = async (payment, bill) => {
    setGeneratingReceipt(payment._id);
    try {
      // Fetch images using proxy
      const [logoBase64, signatureBase64] = await Promise.all([
        fetchImageAsBase64(schoolInfo?.logo),
        fetchImageAsBase64(schoolInfo?.headteacherSignature || schoolInfo?.headTeacherSignature)
      ]);

      // Generate verifiable QR code
      const qrData = JSON.stringify({
        doc: 'Receipt',
        rec: payment._id,
        stu: bill.student?.name,
        amt: payment.amount,
        dt: new Date(payment.date || payment.createdAt).toLocaleDateString('en-GB')
      });
      const qrBase64 = await QRCode.toDataURL(qrData, {
        width: 150,
        margin: 1,
        color: { dark: '#000000', light: '#ffffff' }
      });

      const blob = await pdf(
        <ReceiptPDF
          payment={payment}
          schoolInfo={schoolInfo}
          bill={bill}
          logoBase64={logoBase64}
          signatureBase64={signatureBase64}
          qrBase64={qrBase64}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt_${payment._id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating receipt:', error);
      toast.error('Failed to generate receipt');
    } finally {
      setGeneratingReceipt(null);
    }
  };

  const handleOpenEditBill = (billToEdit) => {
    const transformedBill = transformBill(billToEdit);
    const isPlaceholder = billToEdit.isPlaceholder;

    setExistingBill(isPlaceholder ? null : transformedBill);
    setBillDialog({
      open: true,
      mode: isPlaceholder ? 'create' : 'edit',
      bill: transformedBill,
      studentId: transformedBill.student?._id,
      items: transformedBill.items.length > 0
        ? transformedBill.items.map(item => ({
          name: item.name || 'Unnamed Fee',
          amount: item.amount,
          paid: item.paid,
          balance: item.balance,
        }))
        : feeTemplates[0]?.items.map(item => ({
          name: item.name,
          amount: item.amount,
          paid: 0,
          balance: item.amount
        })) || [{ name: '', amount: '', paid: 0, balance: 0 }],
      paymentAmount: 0,
      paymentNote: ''
    });
  };

  const handleRecordPayment = async () => {
    if (!billDialog.bill || !Number.isFinite(Number(billDialog.paymentAmount)) || Number(billDialog.paymentAmount) <= 0) {
      toast.error('Enter a valid payment amount');
      return;
    }
    setLoading(true);

    const paymentAmount = Number(billDialog.paymentAmount);
    const billId = billDialog.bill._id;
    const isDailyVariable = isDailyVariableBill(billDialog.bill);

    try {
      // 1. Perform API call
      const res = await api.post('/fees/payments', {
        billId: billId,
        amount: paymentAmount,
        method: 'Cash',
        term,
        academicYear,
        studentId: billDialog.bill.student?._id,
        note: billDialog.paymentNote || ''
      });

      // 2. Optimistic / Local Update
      // We know exactly what we just paid, so we can update the state 
      // deterministically without relying on potential backend formatting issues.
      setTermBills(prev =>
        prev.map(bill => {
          if (String(bill._id) === String(billId)) {

            // Get current values (safe fallback to 0)
            const currentTotal = Number(bill.totalAmount) || 0;
            const currentPaid = Number(bill.totalPaid) || 0;

            // Calculate new values locally
            const newPaid = currentPaid + paymentAmount;
            const newTotal = isDailyVariable ? newPaid : currentTotal;
            const newBalance = isDailyVariable ? 0 : newTotal - newPaid;

            // Determine status locally
            const newStatus = isDailyVariable ? 'Paid' : (newBalance <= 0.1 ? 'Paid' : 'Partial');

            // Merge with backend response if available, but prioritize our local math for critical fields
            const backendBill = res.data.updatedBill ? transformBill(res.data.updatedBill) : {};

            // Ensure we have a valid new payment object to display immediately
            const newPaymentEntry = res.data.payment || {
              _id: `temp-${Date.now()}`,
              amount: paymentAmount,
              date: new Date().toISOString(),
              method: 'Cash'
            };

            // Force update the payments array so the "Print Receipt" button appears
            // We append the new payment to the existing list
            const currentPayments = Array.isArray(bill.payments) ? bill.payments : [];
            const updatedPayments = [...currentPayments, newPaymentEntry];

            return {
              ...bill,
              ...backendBill, // Merge backend data (like items updates)

              // Explicitly update payments list
              payments: updatedPayments,

              // OVERRIDE financial fields with our local math to ensure 
              // the UI reflects exactly what the user just did.
              totalAmount: newTotal,
              totalPaid: newPaid,
              balance: newBalance,
              status: newStatus,
              billingMode: bill.billingMode,
              isDailyVariable: bill.isDailyVariable,
              dailyFeeLabel: bill.dailyFeeLabel,

              // Preserve student/class data
              student: bill.student,
              class: bill.class
            };
          }
          return bill;
        })
      );

      // Close dialog and show success snackbar
      setBillDialog({ ...billDialog, open: false });
      toast.success('Payment recorded successfully');

    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error('Failed to record payment');
      // If error, reload to ensure state is consistent
      fetchTermBills();
    } finally {
      setLoading(false);
    }
  };

  const handleReversePayment = (payment, bill) => {
    setReverseDialog({ open: true, payment, bill, loading: false });
  };

  const confirmReversePayment = async () => {
    const { payment, bill } = reverseDialog;
    if (!payment || !bill) return;

    setReverseDialog(prev => ({ ...prev, loading: true }));
    try {
      const res = await api.delete(`/fees/payments/${payment._id}/bill/${bill._id}`);
      
      const serverBill = res.data.updatedBill;
      const savedBill = {
        ...transformBill(serverBill),
        student: serverBill.student || bill.student,
        class: serverBill.class || bill.class
      };
      
      setTermBills(prev => prev.map(current => 
        String(current._id) === String(savedBill._id) ? savedBill : current
      ));
      
      toast.success('Payment reversed successfully');
      fetchSchoolWideSummary();
      setReverseDialog({ open: false, payment: null, bill: null, loading: false });
    } catch (error) {
      console.error('Error reversing payment:', error);
      toast.error(error.response?.data?.message || 'Failed to reverse payment');
      fetchTermBills();
      setReverseDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleToggleExemption = async (studentId, currentStatus) => {
    try {
      // Optimistic update
      setTermBills(prev => prev.map(bill => {
        if (bill.student?._id === studentId || bill.studentId === studentId || bill._id === `temp-${studentId}`) {
          const newExemptStatus = !currentStatus;
          return {
            ...bill,
            isExemptFromTermFees: newExemptStatus
          };
        }
        return bill;
      }));

      await api.post(`/fees/bills/exempt/${studentId}`);
      toast.success(`Student ${!currentStatus ? 'exempted from' : 'liable for'} term fees`);
      fetchSchoolWideSummary();
    } catch (error) {
      console.error('Error toggling exemption:', error);
      toast.error('Failed to update exemption status');
      // Revert on failure
      fetchTermBills();
    }
  };

  const handleSetBillingMode = async (bill, nextMode) => {
    const studentId = bill.student?._id || bill.studentId || (bill._id || '').replace('temp-', '');
    if (!studentId) {
      toast.error('Student not found');
      return;
    }

    try {
      setLoading(true);
      const res = await api.post(`/fees/bills/billing-mode/${studentId}`, {
        billingMode: nextMode,
        term,
        academicYear,
        templateId: selectedTemplateId
      });

      if (res.data?.bill) {
        const updatedBill = transformBill(res.data.bill);
        setTermBills(prev => prev.map(current => {
          const currentStudentId = current.student?._id || current.studentId || (current._id || '').replace('temp-', '');
          return String(currentStudentId) === String(studentId)
            ? {
              ...current,
              ...updatedBill,
              student: current.student,
              class: current.class,
              termFeeBillingMode: res.data.termFeeBillingMode || nextMode,
              billingMode: updatedBill.billingMode || nextMode,
              isDailyVariable: isDailyVariableBill(updatedBill) || nextMode === 'daily-variable'
            }
            : current;
        }));
      } else {
        setTermBills(prev => prev.map(current => {
          const currentStudentId = current.student?._id || current.studentId || (current._id || '').replace('temp-', '');
          return String(currentStudentId) === String(studentId)
            ? {
              ...current,
              termFeeBillingMode: res.data?.termFeeBillingMode || nextMode,
              billingMode: current.isPlaceholder ? nextMode : current.billingMode,
              isDailyVariable: current.isPlaceholder ? nextMode === 'daily-variable' : isDailyVariableBill(current)
            }
            : current;
        }));
      }

      toast.success(nextMode === 'daily-variable' ? 'Student set as daily fee payer' : 'Student set as fixed fee payer');
      fetchTermBills();
      fetchSchoolWideSummary();
    } catch (error) {
      console.error('Error updating billing mode:', error);
      toast.error(error.response?.data?.message || 'Failed to update billing mode');
      fetchTermBills();
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBill = useCallback(async () => {
    setLoading(true);
    try {
      if (!billDialog.studentId) {
        throw new Error('Student must be selected');
      }
      if (!selectedTemplateId) {
        throw new Error('Fee template must be selected');
      }
      if (isDailyVariableBill(billDialog.bill)) {
        throw new Error('Daily fee payer bills are updated through daily payments');
      }

      const processedItems = billDialog.items.map(item => ({
        name: item.name || 'Unnamed Fee',
        amount: Number(item.amount) || 0,
        paid: Number(item.paid) || 0,
        balance: (Number(item.amount) || 0) - (Number(item.paid) || 0)
      }));

      const totalAmount = processedItems.reduce((sum, item) => sum + item.amount, 0);
      const totalPaid = processedItems.reduce((sum, item) => sum + item.paid, 0);
      const status = totalPaid >= totalAmount ? 'Paid' :
        totalPaid > 0 ? 'Partial' : 'Unpaid';

      const payload = {
        studentId: billDialog.studentId,
        templateId: selectedTemplateId,
        items: processedItems,
        term: term || 'Term 1',
        academicYear: academicYear || new Date().getFullYear().toString(),
        totalAmount,
        totalPaid,
        status,
        isManualUpdate: true
      };

      if (billDialog.mode === 'edit' && existingBill?._id) {
        payload.billId = existingBill._id;
      }

      const res = await api.put('/fees/bills/update-or-create', payload);

      // Use the newly created/updated bill from server, fallback gracefully
      const serverBill = res.data.bill || res.data;
      const savedBill = {
        ...transformBill(serverBill),
        student: serverBill.student || existingBill?.student || { _id: billDialog.studentId, name: 'Unknown' },
        class: serverBill.class || existingBill?.class || {},
        isManualUpdate: true
      };

      setTermBills(prevBills => {
        if (billDialog.mode === 'edit') {
          return prevBills.map(bill =>
            bill._id === savedBill._id ? savedBill : bill
          );
        } else {
          return [...prevBills, savedBill];
        }
      });

      toast.success(
        billDialog.mode === 'edit'
          ? 'Bill updated successfully'
          : 'Bill created successfully'
      );

      setBillDialog({ ...billDialog, open: false });

    } catch (error) {
      console.error('Error saving bill:', error);
      toast.error('Failed to save bill');
    } finally {
      setLoading(false);
    }
  }, [
    billDialog,
    selectedTemplateId,
    term,
    academicYear,
    existingBill
  ]);

  const handlePreviewBills = useCallback(async () => {
    if (!selectedClassId || !selectedTemplateId || !term || !academicYear) {
      toast.error('Please select all required fields');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/fees/bills/preview', {
        classId: selectedClassId, templateId: selectedTemplateId, term, academicYear
      });
      setPreviewData({
        ...res.data,
        preview: (res.data.preview || []).sort((a, b) => {
          const nameA = (a.studentName || '').toUpperCase();
          const nameB = (b.studentName || '').toUpperCase();
          return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
        })
      });
      setPreviewDialogOpen(true);
    } catch (error) {
      console.error('Error previewing bills:', error);
      toast.error('Failed to preview bills');
    } finally {
      setLoading(false);
    }
  }, [selectedClassId, selectedTemplateId, term, academicYear]);

  const handleGenerateBills = useCallback(async () => {
    setLoading(true);
    try {
      const missingFields = [];
      if (!selectedClassId) missingFields.push('a class');
      if (!selectedTemplateId) missingFields.push('a fee template');
      if (!term?.trim()) missingFields.push('a term');
      if (!academicYear?.trim()) missingFields.push('an academic year');

      if (missingFields.length > 0) {
        toast.error(`Please select ${missingFields.join(', ')}`);
        return;
      }

      const manualBills = termBills
        .filter(bill => bill.isManualUpdate && !isDailyVariableBill(bill))
        .map(bill => {
          const validItems = bill.items
            .filter(item => item.name && !isNaN(item.amount))
            .map(item => ({
              name: item.name.trim(),
              amount: Number(item.amount),
              paid: Number(item.paid) || 0,
              balance: Number(item.balance) || Number(item.amount)
            }));

          const totalAmount = validItems.reduce((sum, item) => sum + item.amount, 0);
          const totalPaid = validItems.reduce((sum, item) => sum + item.paid, 0);
          const status = totalPaid >= totalAmount ? 'Paid' : totalPaid > 0 ? 'Partial' : 'Unpaid';

          return {
            studentId: bill.student?._id,
            items: validItems,
            totalAmount,
            totalPaid,
            status
          };
        });

      const res = await api.post('/fees/bills/generate', {
        classId: selectedClassId,
        templateId: selectedTemplateId,
        term: term.trim(),
        academicYear: academicYear.trim(),
        manualBills
      });

      if (!res.data.success) {
        throw new Error(res.data.message || 'Failed to generate bills');
      }

      const formatCurrency = (amount) =>
        new Intl.NumberFormat('en-GH', {
          style: 'currency',
          currency: 'GHS'
        }).format(amount || 0);

      const transformedBills = res.data.bills.map(bill => ({
        ...bill,
        items: bill.items?.map(item => ({
          ...item,
          formattedAmount: formatCurrency(item.amount),
          formattedPaid: formatCurrency(item.paid),
          formattedBalance: formatCurrency(item.balance)
        })) || [],
        formattedTotal: formatCurrency(bill.totalAmount),
        formattedPaid: formatCurrency(bill.totalPaid),
        formattedBalance: formatCurrency(bill.totalAmount - (bill.totalPaid || 0))
      }));

      setHasGenerated(true);
      setTermBills(transformedBills);
      toast.success(`Successfully generated ${res.data.count} bills`);
      setPreviewDialogOpen(false);

    } catch (error) {
      console.error('Bill Generation Error:', error);
      toast.error('Failed to generate bills');
    } finally {
      setLoading(false);
    }
  }, [selectedClassId, selectedTemplateId, term, academicYear, termBills]);

  // Initialize data
  useEffect(() => {
    if (schoolId) {
      // Aggressively pre-fill current term and year using the global timeline endpoint
      api.get('/term/current-week', { params: { school: schoolId } })
        .then(res => {
          const cw = res.data?.data || res.data;
          if (userSelectedAcademicYearRef.current) return;
          if (cw?.academicYear && cw?.termName) {
            setAcademicYear(cw.academicYear);
            setTerm(cw.termName);
          }
        }).catch(err => console.error('Auto-select error:', err));

      fetchAcademicYears();
      fetchFeeTemplates();
    }
  }, [schoolId, fetchAcademicYears, fetchFeeTemplates]);

  useEffect(() => {
    if (academicYear) fetchTerms();
  }, [academicYear, fetchTerms]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  useEffect(() => {
    fetchTermBills();
    fetchSchoolWideSummary();
  }, [fetchTermBills, fetchSchoolWideSummary]);

  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : 'S';

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid': return 'success';
      case 'Partial': return 'warning';
      default: return 'error';
    }
  };

  // Mobile Bill Card Component - Moved outside or memoized (or simplified as is)
  // Fix: rely on props directly instead of re-calculating with defaults that might override 0
  const MobileBillCard = ({ bill }) => {
    const studentName = bill.student?.name || 'N/A';
    const className =
      bill.class?.displayName || bill.class?.name || 'N/A';
    const termDisplay = `${bill.term || 'N/A'} ${bill.academicYear || ''}`.trim();

    // Direct usage of bill properties which we now guarantee are numbers
    const amount = bill.totalAmount;
    const paid = bill.totalPaid;
    const balance = bill.balance;
    const isPlaceholder = bill.isPlaceholder;
    const isExempt = bill.isExemptFromTermFees;
    const isDailyVariable = isDailyVariableBill(bill);

    const status = isExempt ? 'EXEMPT' : (isDailyVariable ? 'Daily Payer' : (isPlaceholder ? 'No Bill' : (bill.status || 'Unpaid')));
    const lastPayment = bill.payments?.[bill.payments.length - 1];

    return (
      <Card sx={{
        mb: 2,
        boxShadow: 2,
        opacity: isPlaceholder ? 0.8 : 1,
        bgcolor: isPlaceholder ? alpha(theme.palette.action.disabledBackground, 0.04) : 'background.paper'
      }}>
        <CardContent sx={{ p: 2 }}>
          <Stack spacing={2}>
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack direction="row" alignItems="center" spacing={1}>
                <StudentAvatar student={bill.student} sx={{
                  bgcolor: isPlaceholder ? theme.palette.action.disabled : theme.palette.primary.main,
                  width: 40,
                  height: 40
                }}>
                  {getInitials(studentName)}
                </StudentAvatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="600" color={isPlaceholder ? 'text.secondary' : 'text.primary'}>
                    {studentName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {className} • {termDisplay}
                  </Typography>
                </Box>
              </Stack>
              <Chip
                label={status}
                color={isExempt ? "secondary" : (isDailyVariable ? "info" : (isPlaceholder ? "default" : getStatusColor(status)))}
                size="small"
                variant={isPlaceholder ? "outlined" : "filled"}
                sx={{ fontWeight: 600 }}
              />
            </Stack>

            {/* Financial Info */}
            <Grid container spacing={2}>
              <Grid size={4}>
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    TOTAL
                  </Typography>
                  <Typography variant="body2" fontWeight="600" color={isPlaceholder ? 'text.secondary' : 'text.primary'}>
                    {isPlaceholder && !isDailyVariable ? '—' : `₵${amount.toFixed(2)}`}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={4}>
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    PAID
                  </Typography>
                  <Typography variant="body2" fontWeight="600" color={isPlaceholder ? 'text.secondary' : 'success.main'}>
                    {isPlaceholder ? '—' : `₵${paid.toFixed(2)}`}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={4}>
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {isDailyVariable ? 'MODE' : 'BALANCE'}
                  </Typography>
                  <Typography variant="body2" fontWeight="600" color={isPlaceholder ? 'text.secondary' : (balance > 0 ? 'error.main' : 'success.main')}>
                    {isDailyVariable ? 'Daily' : (isPlaceholder ? '—' : `₵${balance.toFixed(2)}`)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <ActionToolbar direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-start" sx={{ mt: 1 }}>
              <Tooltip title={isExempt ? "Student is exempt" : (isDailyVariable ? "Daily fee bills are updated by payments" : "Edit Bill")}>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => handleOpenEditBill(bill)}
                    sx={{ flex: 1 }}
                    disabled={isExempt || isDailyVariable}
                  >
                    Edit
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={isExempt ? "Student is exempt" : "Record Payment"}>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Payment />}
                    onClick={() => setBillDialog({
                      open: true,
                      mode: 'pay',          // ← IMPORTANT
                      bill: bill,
                      studentId: bill.student?._id,
                      items: bill.items.map(item => ({
                        name: item.name,
                        amount: item.amount,
                        paid: item.paid,
                        balance: item.balance
                      })),
                      paymentAmount: 0,
                      paymentNote: ''
                    })}
                    sx={{ flex: 1 }}
                    disabled={isPlaceholder || isExempt}
                  >
                    {isDailyVariable ? 'Daily Fee' : 'Pay'}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={isExempt ? "Student is exempt" : (isPlaceholder ? "Create bill first" : "Fee Statement")}>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Description />}
                    onClick={() => handleFeeStatementClick(bill)}
                    disabled={isPlaceholder || !!generatingFeeStatement || isExempt}
                    sx={{ flex: 1 }}
                  >
                    {generatingFeeStatement === bill._id ? (
                      <CircularProgress size={16} />
                    ) : (
                      'Statement'
                    )}
                  </Button>
                </span>
              </Tooltip>
              {lastPayment && (
                <>
                  <Tooltip title="Print Receipt">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Print />}
                      onClick={() => handlePrintReceiptClick(lastPayment, bill)}
                      disabled={!!generatingReceipt}
                      sx={{ flex: 1 }}
                    >
                      {generatingReceipt === lastPayment._id ? (
                        <CircularProgress size={16} />
                      ) : (
                        'Receipt'
                      )}
                    </Button>
                  </Tooltip>
                  <Tooltip title="Reverse Payment">
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handleReversePayment(lastPayment, bill)}
                      disabled={loading}
                      sx={{ minWidth: 40, px: 0, ml: 1 }}
                    >
                      <Delete fontSize="small" />
                    </Button>
                  </Tooltip>
                </>
              )}
              <Tooltip title={isDailyVariable ? "Switch to fixed billing" : "Switch to daily variable billing"}>
                <span>
                  <Button
                    variant={isDailyVariable ? "contained" : "outlined"}
                    size="small"
                    onClick={() => handleSetBillingMode(bill, isDailyVariable ? 'fixed' : 'daily-variable')}
                    disabled={isExempt || loading}
                    sx={{ flex: 1 }}
                  >
                    Daily
                  </Button>
                </span>
              </Tooltip>
            </ActionToolbar>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: isMobile ? 2 : 4,
        px: isMobile ? 1 : 2
      }}
    >
      {/* Header */}
      <PageHeader title={<>Term Billing Management</>} description={<>Manage student fees, bills, and payments</>} actions={<><Button variant="outlined" startIcon={<Preview />} onClick={handlePreviewBills} disabled={loading || !selectedClassId || !selectedTemplateId || !term || !academicYear} fullWidth={isMobile} size={isMobile ? "medium" : "large"}>
                        Preview Bills
                      </Button>
        <Button variant="contained" startIcon={<CloudUpload />} onClick={handleGenerateBills} disabled={loading || !selectedClassId || !selectedTemplateId || !term || !academicYear} fullWidth={isMobile} size={isMobile ? "medium" : "large"}>
                        Generate Bills
                      </Button>
        <Button variant="outlined" startIcon={showTemplateForm ? <ExpandLess /> : <ExpandMore />} onClick={() => setShowTemplateForm(!showTemplateForm)} fullWidth={isMobile} size={isMobile ? "medium" : "large"}>
                        {showTemplateForm ? 'Hide Template' : 'Create Template'}
                      </Button>
        <Button variant="outlined" color="secondary" startIcon={<Description />} onClick={() => setShowAuditDialog(true)} fullWidth={isMobile} size={isMobile ? "medium" : "large"}>
                        Audit Report
                      </Button></>}></PageHeader>
      {schoolWideSummary && (
        <Card sx={{
          mb: 4,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          border: `1px solid ${theme.palette.primary.main}`
        }}>
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" mb={2} spacing={1}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} color="primary.main" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <School fontSize={isMobile ? "small" : "medium"} /> School-Wide Term Collection ({term} • {academicYear})
              </Typography>
              <Chip
                label={`Billing Rate: ${schoolWideSummary.totalExpected > 0 ? ((schoolWideSummary.totalPaid / schoolWideSummary.totalExpected) * 100).toFixed(1) : 0}%`}
                color="primary"
                size={isMobile ? "small" : "medium"}
                sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.85rem' : '1rem' }}
              />
            </Stack>

            <SummaryStrip container spacing={isMobile ? 1 : 2} justifyContent="center" columns={{ xs: 12, sm: 10 }}>
              <Grid size={{ xs: 6, sm: 2 }}>
                <SummaryMetric elevation={0} sx={{ p: isMobile ? 1 : 2, textAlign: 'center', border: `1px solid ${theme.palette.divider}` }}>
                  <Typography variant="caption" display="block" color="text.secondary" fontWeight="bold">EXPECTED</Typography>
                  <Typography variant="h4" color="primary.main" fontWeight="bold">GH₵{(schoolWideSummary.totalExpected || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</Typography>
                </SummaryMetric>
              </Grid>
              <Grid size={{ xs: 6, sm: 2 }}>
                <SummaryMetric elevation={0} sx={{ p: isMobile ? 1 : 2, textAlign: 'center', border: `1px solid ${theme.palette.divider}` }}>
                  <Typography variant="caption" display="block" color="text.secondary" fontWeight="bold">COLLECTED</Typography>
                  <Typography variant="h4" color="success.main" fontWeight="bold">GH₵{(schoolWideSummary.totalPaid || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</Typography>
                </SummaryMetric>
              </Grid>
              <Grid size={{ xs: 6, sm: 2 }}>
                <SummaryMetric elevation={0} sx={{ p: isMobile ? 1 : 2, textAlign: 'center', border: `1px solid ${theme.palette.divider}` }}>
                  <Typography variant="caption" display="block" color="text.secondary" fontWeight="bold">ARREARS</Typography>
                  <Typography variant="h4" color="error.main" fontWeight="bold">GH₵{(schoolWideSummary.totalBalance || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</Typography>
                </SummaryMetric>
              </Grid>
              <Grid size={{ xs: 6, sm: 2 }}>
                <SummaryMetric elevation={0} sx={{ p: isMobile ? 1 : 2, textAlign: 'center', border: `1px solid ${theme.palette.divider}` }}>
                  <Typography variant="caption" display="block" color="text.secondary" fontWeight="bold">BILLED</Typography>
                  <Typography variant="h4" color="text.primary" fontWeight="bold">{schoolWideSummary.studentsBilled}</Typography>
                </SummaryMetric>
              </Grid>
              <Grid size={{ xs: 6, sm: 2 }}>
                <SummaryMetric elevation={0} sx={{ p: isMobile ? 1 : 2, textAlign: 'center', border: `1px solid ${theme.palette.divider}` }}>
                  <Typography variant="caption" display="block" color="text.secondary" fontWeight="bold">FULLY PAID</Typography>
                  <Typography variant="h4" color="success.main" fontWeight="bold">{schoolWideSummary.fullyPaidCount}</Typography>
                </SummaryMetric>
              </Grid>
            </SummaryStrip>
          </CardContent>
        </Card>
      )}

      {/* Filter Card */}
      <FilterBar sx={{
        mb: 4,
        boxShadow: isMobile ? 1 : 3,
        border: `1px solid ${theme.palette.divider}`
      }}>
        <CardContent sx={{ p: isMobile ? 2 : isTablet ? 3 : 4 }}>
          <Stack
            direction={isMobile ? "column" : "row"}
            alignItems={isMobile ? "flex-start" : "center"}
            spacing={isMobile ? 2 : 2}
            sx={{ mb: 3 }}
          >
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: isMobile ? 40 : 48,
                height: isMobile ? 40 : 48
              }}
            >
              <CalendarToday fontSize={isMobile ? "small" : "medium"} />
            </Avatar>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              fontWeight="600"
              sx={{ fontSize: isMobile ? '1.125rem' : '1.5rem' }}
            >
              Billing Setup
            </Typography>
          </Stack>

          <Grid container spacing={isMobile ? 2 : 3}>
            {/* Academic Year */}
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                fullWidth
                label="Academic Year"
                value={academicYear}
                onChange={(e) => {
                  userSelectedAcademicYearRef.current = true;
                  setAcademicYear(e.target.value);
                  setTerm('');
                }}
                disabled={loading || availableAcademicYears.length === 0}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: academicYear !== '' ? undefined : () => 'Select Academic Year'
                }}
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="" disabled>Select Academic Year</MenuItem>
                {availableAcademicYears.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Term */}
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                fullWidth
                label="Academic Term"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                disabled={loading || availableTerms.length === 0}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: term !== '' ? undefined : () => 'Select Academic Term'
                }}
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="" disabled>Select Academic Term</MenuItem>
                {availableTerms.map(term => (
                  <MenuItem key={term} value={term}>{term}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Class */}
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                fullWidth
                label="Class"
                value={selectedClassId}
                onChange={(e) => setSelectedClassId(e.target.value)}
                disabled={loading || classes.length === 0}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: selectedClassId !== '' ? undefined : () => 'Select Class'
                }}
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="" disabled>Select Class</MenuItem>
                {classes.map(cls => (
                  <MenuItem key={cls._id} value={cls._id}>
                    {cls.displayName || cls.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Fee Template */}
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                fullWidth
                label="Fee Template"
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                disabled={loading || feeTemplates.length === 0}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: selectedTemplateId !== '' ? undefined : () => 'Select Fee Template'
                }}
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="" disabled>Select Fee Template</MenuItem>
                {feeTemplates.map(template => (
                  <MenuItem key={template._id} value={template._id}>{template.name}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Action Buttons */}

          </Grid>
        </CardContent>
      </FilterBar>

      {/* Template Form Card */}
      <Collapse in={showTemplateForm}>
        <Card sx={{
          mb: 4,
          boxShadow: isMobile ? 1 : 3,
          border: `1px solid ${theme.palette.divider}`
        }}>
          <CardContent sx={{ p: isMobile ? 2 : isTablet ? 3 : 4 }}>
            <Stack
              direction={isMobile ? "column" : "row"}
              alignItems={isMobile ? "flex-start" : "center"}
              spacing={isMobile ? 2 : 2}
              sx={{ mb: 3 }}
            >
              <Avatar
                sx={{
                  bgcolor: theme.palette.secondary.main,
                  width: isMobile ? 40 : 48,
                  height: isMobile ? 40 : 48
                }}
              >
                <Receipt fontSize={isMobile ? "small" : "medium"} />
              </Avatar>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                fontWeight="600"
                sx={{ fontSize: isMobile ? '1.125rem' : '1.5rem' }}
              >
                Create Fee Template
              </Typography>
            </Stack>

            <Grid container spacing={isMobile ? 2 : 3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Template Name"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  disabled={loading}
                  placeholder="Enter template name"
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={isMobile ? 3 : 4}
                  label="Bulk Add Items (Name:Amount)"
                  placeholder="Tuition:500\nLibrary Fee:200\nSports:100"
                  value={bulkItemsInput}
                  onChange={(e) => setBulkItemsInput(e.target.value)}
                  helperText="Enter items in format: Item Name:Amount (one per line)"
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (!bulkItemsInput.trim()) {
                      toast.error("No items entered!");
                      return;
                    }
                    const lines = bulkItemsInput.split("\n");
                    const parsedItems = lines.map(line => {
                      const [name, amount] = line.split(":").map(s => s.trim());
                      return name && !isNaN(amount) ? { name, amount: Number(amount), isMandatory: true } : null;
                    }).filter(Boolean);
                    if (parsedItems.length === 0) {
                      toast.error("No valid items found. Use 'Name:Amount' format.");
                      return;
                    }
                    setTemplateItems(parsedItems);
                    toast.success(`Added ${parsedItems.length} items!`);
                  }}
                  disabled={!bulkItemsInput.trim()}
                  fullWidth={isMobile}
                  size={isMobile ? "small" : "medium"}
                >
                  Parse Items
                </Button>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2 }}>OR</Divider>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  label="Item Name"
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  disabled={loading}
                  placeholder="Enter item name"
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  value={newItemAmount}
                  onChange={(e) => setNewItemAmount(e.target.value)}
                  disabled={loading}
                  placeholder="Enter amount"
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newItemMandatory}
                      onChange={(e) => setNewItemMandatory(e.target.checked)}
                      disabled={loading}
                      size={isMobile ? "small" : "medium"}
                    />
                  }
                  label="Mandatory"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    if (!newItemName || !newItemAmount) {
                      toast.error('Item name and amount are required');
                      return;
                    }
                    setTemplateItems(prev => [
                      ...prev, { name: newItemName, amount: Number(newItemAmount), isMandatory: newItemMandatory }
                    ]);
                    setNewItemName('');
                    setNewItemAmount('');
                    setNewItemMandatory(true);
                  }}
                  disabled={loading || !newItemName || !newItemAmount}
                  size={isMobile ? "small" : "medium"}
                >
                  Add Item
                </Button>
              </Grid>

              {/* Template Items Display */}
              <Grid size={{ xs: 12 }}>
                {templateItems.length > 0 ? (
                  <Card variant="outlined" sx={{ mt: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom fontWeight="600">
                        Template Items ({templateItems.length})
                      </Typography>
                      <Stack spacing={1}>
                        {templateItems.map((item, index) => (
                          <Box key={index} display="flex" alignItems="center" justifyContent="space-between">
                            <Box>
                              <Typography variant="body1" fontWeight="500">
                                {item.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                ₵{item.amount} {item.isMandatory && '• Mandatory'}
                              </Typography>
                            </Box>
                            <IconButton
                              color="error"
                              onClick={() => setTemplateItems(prev => prev.filter((_, i) => i !== index))}
                              size="small"
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                ) : (
                  <Alert severity="info" sx={{ borderRadius: isMobile ? 1 : 2 }}>
                    No items added yet. Add items using the form above.
                  </Alert>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  onClick={async () => {
                    if (!newTemplateName || templateItems.length === 0) {
                      toast.error('Template name and at least one item are required');
                      return;
                    }
                    setLoading(true);
                    try {
                      const res = await api.post('/fees/fee-templates', { name: newTemplateName, items: templateItems });
                      setFeeTemplates(prev => [...prev, res.data]);
                      toast.success('Fee template created successfully');
                      setNewTemplateName('');
                      setTemplateItems([]);
                      setBulkItemsInput('');
                      setShowTemplateForm(false);
                    } catch (error) {
                      console.error('Error creating fee template:', error);
                      toast.error('Failed to create fee template');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading || !newTemplateName || templateItems.length === 0}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                  fullWidth={isMobile}
                  size={isMobile ? "medium" : "large"}
                >
                  Save Fee Template
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Collapse>

      {/* 🏫 School-Wide Term Collection Summary */}
      

      {/* Bills Table Card */}
      <Card sx={{
        boxShadow: isMobile ? 1 : 3,
        border: `1px solid ${theme.palette.divider}`
      }}>
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: isMobile ? 2 : 3,
              borderBottom: `1px solid ${theme.palette.divider}`
            }}
          >
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent="space-between"
              alignItems={isMobile ? "flex-start" : "center"}
              spacing={isMobile ? 1 : 0}
            >
              <Typography
                variant={isMobile ? "h6" : "h6"}
                fontWeight="600"
                sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
              >
                Student Bills ({termBills.length})
              </Typography>
              {termBills.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    label={`Total: ₵${termBills.reduce((sum, bill) => sum + (bill.isExemptFromTermFees ? 0 : (bill.totalAmount || 0)), 0).toFixed(2)}`}
                    color="primary"
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                  />
                  <Chip
                    label={`Paid: ₵${termBills.reduce((sum, bill) => sum + (bill.isExemptFromTermFees ? 0 : (bill.totalPaid || 0)), 0).toFixed(2)}`}
                    color="success"
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                  />
                  <Chip
                    label={`Balance: ₵${termBills.reduce((sum, bill) => sum + (bill.isExemptFromTermFees ? 0 : (typeof bill.balance === 'number' ? bill.balance : (bill.totalAmount || 0) - (bill.totalPaid || 0))), 0).toFixed(2)}`}
                    color="error"
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                  />
                </Stack>
              )}
            </Stack>
          </Box>

          {hasGenerated || termBills.length > 0 ? (
            termBills.length > 0 ? (
              isMobile ? (
                // Mobile View - Cards
                <Box sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    {sortedTermBills.map((bill) => (
                      <MobileBillCard
                        key={bill._id}
                        bill={{
                          ...bill,
                          // Pass explicitly calculated financial fields
                          totalAmount: typeof bill.totalAmount === 'number' ? bill.totalAmount : 0,
                          totalPaid: typeof bill.totalPaid === 'number' ? bill.totalPaid : 0,
                          balance: typeof bill.balance === 'number' ? bill.balance : (bill.totalAmount || 0) - (bill.totalPaid || 0),
                          status: bill.status || 'Unpaid'
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              ) : (
                // Desktop/Tablet View - Table
                <Box sx={{
                  width: '100%',
                  overflow: 'auto',
                  maxHeight: isTablet ? '60vh' : 'none'
                }}>
                  <DataTable><Table sx={{ minWidth: isTablet ? 800 : 'auto' }}>
                    <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', py: 2 }}>STUDENT</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', py: 2 }}>CLASS</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', py: 2 }}>TERM</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="right">AMOUNT</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="right">BALANCE</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', py: 2 }}>STATUS</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', py: 2, textAlign: 'center' }}>ACTIONS</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
{sortedTermBills.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                      {sortedTermBills.map((bill) => {
                        const studentName = bill.student?.name || 'N/A';
                        const className =
                          bill.class?.displayName || bill.class?.name || 'N/A';
                        const termDisplay = `${bill.term || 'N/A'} ${bill.academicYear || ''}`.trim();
                        const amount = typeof bill.totalAmount === 'number' ? bill.totalAmount : 0;
                        const paid = typeof bill.totalPaid === 'number' ? bill.totalPaid : 0;
                        const balance = typeof bill.balance === 'number' ? bill.balance : amount - paid;
                        const isPlaceholder = bill.isPlaceholder;
                        const isExempt = bill.isExemptFromTermFees;
                        const isDailyVariable = isDailyVariableBill(bill);
                        const status = isExempt ? 'EXEMPT' : (isDailyVariable ? 'Daily Payer' : (isPlaceholder ? 'No Bill' : (bill.status || 'Unpaid')));
                        const lastPayment = bill.payments?.[bill.payments.length - 1];

                        return (
                          <TableRow
                            key={bill._id}
                            sx={{
                              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                              opacity: isPlaceholder ? 0.7 : 1,
                              bgcolor: isPlaceholder ? alpha(theme.palette.action.disabledBackground, 0.04) : 'inherit'
                            }}
                          >
                            <TableCell sx={{ py: 2 }}>
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <StudentAvatar student={bill.student} sx={{
                                  bgcolor: isPlaceholder ? theme.palette.action.disabled : theme.palette.primary.main,
                                  width: 40, height: 40
                                }}>
                                  {getInitials(studentName)}
                                </StudentAvatar>
                                <Box>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight="500"
                                    color={isPlaceholder ? 'text.secondary' : 'text.primary'}
                                  >
                                    {studentName}
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Chip
                                label={className}
                                size="small"
                                color={isPlaceholder ? "default" : "primary"}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="body2" color={isPlaceholder ? 'text.secondary' : 'text.primary'}>
                                {termDisplay}
                              </Typography>
                              {isDailyVariable && (
                                <Chip
                                  label="Daily variable"
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  sx={{ mt: 0.5, fontWeight: 600 }}
                                />
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 2 }} align="right">
                              <Typography variant="subtitle1" fontWeight="500" color={isPlaceholder || isExempt ? 'text.secondary' : 'text.primary'}>
                                {(isPlaceholder && !isDailyVariable) || isExempt ? '—' : `₵${amount.toFixed(2)}`}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }} align="right">
                              <Typography
                                variant="subtitle1"
                                fontWeight="500"
                                color={isPlaceholder || isExempt ? 'text.secondary' : (balance > 0 ? 'error.main' : 'success.main')}
                              >
                                {isDailyVariable ? '—' : (isPlaceholder || isExempt ? '—' : `₵${balance.toFixed(2)}`)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Chip
                                label={status}
                                color={isExempt ? "secondary" : (isDailyVariable ? "info" : (isPlaceholder ? "default" : getStatusColor(status)))}
                                variant={isPlaceholder ? "outlined" : "filled"}
                                sx={{ fontWeight: 600, minWidth: 80 }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 2, textAlign: 'center' }}>
                              <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                                <Tooltip title={isExempt ? "Student is exempt" : (isDailyVariable ? "Daily fee bills are updated by payments" : (isPlaceholder ? "Create Bill" : "Edit Bill"))}>
                                  <span>
                                    <IconButton
                                      onClick={() => handleOpenEditBill(bill)}
                                      color="primary"
                                      size="small"
                                      disabled={isExempt || isDailyVariable}
                                    >
                                      {isPlaceholder ? <Add /> : <Edit />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title={isExempt ? "Student is exempt" : (isPlaceholder ? "Create bill first to record payment" : "Record Payment")}>
                                  <span>
                                    <IconButton
                                      onClick={() => setBillDialog({
                                        open: true,
                                        mode: 'pay',
                                        bill: bill,
                                        studentId: bill.student?._id,
                                        items: bill.items.map(item => ({
                                          name: item.name,
                                          amount: item.amount,
                                          paid: item.paid,
                                          balance: item.balance
                                        })),
                                        paymentAmount: 0,
                                        paymentNote: ''
                                      })}
                                      color="secondary"
                                      size="small"
                                      disabled={isPlaceholder || isExempt}
                                    >
                                      <Payment />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title={isDailyVariable ? "Switch to fixed billing" : "Switch to daily variable billing"}>
                                  <span>
                                    <Switch
                                      checked={isDailyVariable}
                                      onChange={() => handleSetBillingMode(bill, isDailyVariable ? 'fixed' : 'daily-variable')}
                                      color="info"
                                      size="small"
                                      disabled={isExempt || loading}
                                    />
                                  </span>
                                </Tooltip>
                                <Tooltip title={isExempt ? "Student is exempt" : (isPlaceholder ? "Create bill first to view statement" : "Fee Statement")}>
                                  <span>
                                    <IconButton
                                      onClick={() => handleFeeStatementClick(bill)}
                                      disabled={isPlaceholder || !!generatingFeeStatement || isExempt}
                                      color="info"
                                      size="small"
                                    >
                                      {generatingFeeStatement === bill._id ? (
                                        <CircularProgress size={20} />
                                      ) : (
                                        <Description />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                {lastPayment && !isExempt && (
                                  <>
                                    <Tooltip title="Print Receipt">
                                      <span>
                                        <IconButton
                                          onClick={() => handlePrintReceiptClick(lastPayment, bill)}
                                          disabled={!!generatingReceipt}
                                          color="success"
                                          size="small"
                                        >
                                          {generatingReceipt === lastPayment._id ? (
                                            <CircularProgress size={20} />
                                          ) : (
                                            <Print />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Reverse Payment">
                                      <span>
                                        <IconButton
                                          onClick={() => handleReversePayment(lastPayment, bill)}
                                          disabled={loading}
                                          color="error"
                                          size="small"
                                        >
                                          <Delete />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </>
                                )}
                                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                                <Tooltip title={isExempt ? "Remove Exemption" : "Exempt Student"}>
                                  <Switch
                                    checked={isExempt}
                                    onChange={() => handleToggleExemption(bill.student?._id || bill.studentId || bill._id.replace('temp-', ''), isExempt)}
                                    color="secondary"
                                    size="small"
                                  />
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table></DataTable>
                </Box>
              )
            ) : (
              <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                <AttachMoney
                  sx={{
                    fontSize: isMobile ? 48 : 64,
                    color: 'text.secondary',
                    opacity: 0.5,
                    mb: 2
                  }}
                />
                <Typography
                  variant={isMobile ? "h6" : "h6"}
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
                >
                  No bills generated
                </Typography>
                <Typography
                  variant={isMobile ? "body2" : "body2"}
                  color="text.secondary"
                  sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
                >
                  Generate bills using the form above
                </Typography>
              </Box>
            )
          ) : (
            <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
              <School
                sx={{
                  fontSize: isMobile ? 48 : 64,
                  color: 'text.secondary',
                  opacity: 0.5,
                  mb: 2
                }}
              />
              <Typography
                variant={isMobile ? "h6" : "h6"}
                color="text.secondary"
                gutterBottom
                sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
              >
                Ready to generate bills
              </Typography>
              <Typography
                variant={isMobile ? "body2" : "body2"}
                color="text.secondary"
                sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
              >
                Select your criteria and generate bills to get started
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Bill Dialog */}
      <Dialog
        open={billDialog.open}
        onClose={() => setBillDialog({ ...billDialog, open: false })}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            m: isMobile ? 0 : 'auto'
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: theme.palette.primary.main,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: isMobile ? 2 : 3
        }}>
          <AttachMoney />
          {billDialog.mode === 'pay'
            ? (isDailyVariableBill(billDialog.bill) ? 'Record Daily Fee' : 'Record Payment')
            : billDialog.mode === 'edit' ? 'Edit Bill' : 'Create New Bill'}
        </DialogTitle>

        <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
          {billDialog.mode === 'create' && (
            <TextField
              select
              fullWidth
              label="Student"
              value={billDialog.studentId}
              onChange={(e) => setBillDialog({ ...billDialog, studentId: e.target.value })}
              sx={{ mb: 3 }}
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                displayEmpty: true,
                renderValue: billDialog.studentId !== '' ? undefined : () => 'Select Student'
              }}
              size={isMobile ? "small" : "medium"}
            >
              <MenuItem value="" disabled>Select Student</MenuItem>
              {students.map(student => (
                <MenuItem key={student._id} value={student._id}>
                  {student.user?.name || student.name || 'Unknown Student'} ({student.admissionNumber || 'N/A'}) -
                  {student.class?.displayName || student.class?.name || 'N/A'}
                </MenuItem>
              ))}
            </TextField>
          )}

          {billDialog.mode !== 'pay' && (
            <>
              <Typography variant="subtitle1" gutterBottom fontWeight="600">
                Fee Items
              </Typography>

              {billDialog.items.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                  <TextField
                    label="Item Name"
                    value={item.name}
                    onChange={(e) => {
                      const newItems = [...billDialog.items];
                      newItems[index].name = e.target.value;
                      setBillDialog({ ...billDialog, items: newItems });
                    }}
                    fullWidth
                    placeholder="Enter item name"
                    size={isMobile ? "small" : "medium"}
                  />
                  <TextField
                    label="Amount"
                    type="number"
                    value={item.amount}
                    onChange={(e) => {
                      const newItems = [...billDialog.items];
                      newItems[index].amount = e.target.value;
                      setBillDialog({ ...billDialog, items: newItems });
                    }}
                    sx={{ width: 120 }}
                    placeholder="0.00"
                    size={isMobile ? "small" : "medium"}
                  />
                  <IconButton
                    onClick={() => {
                      if (billDialog.items.length > 1) {
                        setBillDialog({ ...billDialog, items: billDialog.items.filter((_, i) => i !== index) });
                      }
                    }}
                    disabled={billDialog.items.length <= 1}
                    color="error"
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                </Box>
              ))}

              <Button
                onClick={() => setBillDialog({ ...billDialog, items: [...billDialog.items, { name: '', amount: '', paid: 0, balance: 0 }] })}
                startIcon={<Add />}
                variant="outlined"
                sx={{ mt: 1 }}
                fullWidth={isMobile}
                size={isMobile ? "small" : "medium"}
              >
                Add Item
              </Button>
            </>
          )}

          {billDialog.mode === 'pay' && billDialog.bill && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle1" gutterBottom fontWeight="600">
                {isDailyVariableBill(billDialog.bill) ? 'Daily Fee Payment' : 'Record Payment'}
              </Typography>
              <Alert severity={isDailyVariableBill(billDialog.bill) ? "info" : "success"} sx={{ mb: 2 }}>
                {isDailyVariableBill(billDialog.bill)
                  ? `This student has no fixed term balance. Each payment increases the term total collected. Current collected: ₵${(Number(billDialog.bill.totalPaid) || 0).toFixed(2)}.`
                  : `Outstanding balance: ₵${(Number(billDialog.bill.balance) || 0).toFixed(2)}.`}
              </Alert>
              <TextField
                fullWidth
                label={isDailyVariableBill(billDialog.bill) ? "Daily Fee Amount" : "Payment Amount"}
                type="number"
                value={billDialog.paymentAmount}
                onChange={(e) => setBillDialog({ ...billDialog, paymentAmount: parseFloat(e.target.value) })}
                inputProps={{
                  min: 0,
                  ...(isDailyVariableBill(billDialog.bill) ? {} : { max: billDialog.bill.balance }),
                  step: 0.01
                }}
                placeholder="0.00"
                size={isMobile ? "small" : "medium"}
              />
              {isDailyVariableBill(billDialog.bill) && (
                <TextField
                  fullWidth
                  label="Note (optional)"
                  value={billDialog.paymentNote || ''}
                  onChange={(e) => setBillDialog({ ...billDialog, paymentNote: e.target.value })}
                  placeholder="e.g. Monday daily school fee"
                  size={isMobile ? "small" : "medium"}
                  sx={{ mt: 2 }}
                />
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{
          p: isMobile ? 2 : 3,
          gap: 1,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Button
            onClick={() => setBillDialog({ ...billDialog, open: false })}
            variant="outlined"
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
          >
            Cancel
          </Button>
          {billDialog.mode === 'pay' && billDialog.bill && (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleRecordPayment}
              disabled={
                loading ||
                billDialog.paymentAmount <= 0 ||
                (!isDailyVariableBill(billDialog.bill) && billDialog.paymentAmount > billDialog.bill?.balance)
              }
              startIcon={loading ? <CircularProgress size={20} /> : null}
              fullWidth={isMobile}
              size={isMobile ? "medium" : "large"}
            >
              {isDailyVariableBill(billDialog.bill) ? 'Record Daily Fee' : 'Record Payment'}
            </Button>
          )}
          {billDialog.mode !== 'pay' && (
            <Button
              variant="contained"
              onClick={handleSaveBill}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
              fullWidth={isMobile}
              size={isMobile ? "medium" : "large"}
            >
              {billDialog.mode === 'edit' ? 'Update Bill' : 'Create Bill'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            m: isMobile ? 0 : 'auto'
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: theme.palette.primary.main,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: isMobile ? 2 : 3
        }}>
          <Preview />
          Bill Generation Preview
        </DialogTitle>

        <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
          {previewData && (
            <>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Template: {previewData.templateName}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Total Students: {previewData.count} | Total Amount: ₵{previewData.summary.totalAmount.toFixed(2)}
              </Typography>

              <Card variant="outlined" sx={{ mt: 2 }}>
                <Box sx={{
                  width: '100%',
                  overflow: 'auto',
                  maxHeight: isMobile ? '50vh' : '60vh'
                }}>
                  <DataTable><Table size={isMobile ? "small" : "medium"}>
                    <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Student</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Class</TableCell>
                        {previewData.preview[0]?.items.map(item => (
                          <TableCell key={item.name} align="right" sx={{ fontWeight: 'bold' }}>
                            {item.name} {item.isMandatory && '*'}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewData.preview.map((student, index) => (
                        <TableRow key={index}>
                          <TableCell>{student.studentName}</TableCell>
                          <TableCell>{student.className}</TableCell>
                          {student.items.map((item, idx) => (
                            <TableCell key={idx} align="right">₵{item.amount.toFixed(2)}</TableCell>
                          ))}
                          <TableCell align="right"><strong>₵{student.totalAmount.toFixed(2)}</strong></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table></DataTable>
                </Box>
              </Card>

              <Typography variant="caption" display="block" sx={{ mt: 2 }}>
                * Mandatory items
              </Typography>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{
          p: isMobile ? 2 : 3,
          gap: 1,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Button
            onClick={() => setPreviewDialogOpen(false)}
            variant="outlined"
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateBills}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
          >
            Confirm and Generate Bills
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showAuditDialog}
        onClose={() => setShowAuditDialog(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 2
        }}>
          <Typography variant="h6" fontWeight="bold">
            Daily Term Billing Audit Report
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: isMobile ? '100%' : 'auto' }}>
            <TextField
              type="date"
              size="small"
              value={auditReportDateFilter}
              onChange={(e) => setAuditReportDateFilter(e.target.value)}
              disabled={isFetchingAudit}
              fullWidth={isMobile}
            />
            <Button
              variant="outlined"
              startIcon={<Print />}
              onClick={() => {
                const prevTitle = document.title;
                const schoolName = schoolInfo?.school?.name || 'School';
                document.title = `${schoolName} - Term Billing Audit - ${auditReportDateFilter}`;
                window.print();
                setTimeout(() => { document.title = prevTitle; }, 1000);
              }}
              disabled={!auditReportData || isFetchingAudit}
              fullWidth={isMobile}
            >
              Print
            </Button>
            {isMobile && (
              <IconButton onClick={() => setShowAuditDialog(false)} size="small" sx={{ alignSelf: 'flex-end' }}>
                <Close />
              </IconButton>
            )}
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: isMobile ? 1 : 3 }}>
          {isFetchingAudit ? (
            <LoadingState />
          ) : auditReportData && auditReportData.report ? (
            <>
              {/* Printable Area */}
              <style>
                {`
                @media print {
                  body * { visibility: hidden; }
                  #printable-audit-report, #printable-audit-report * { visibility: visible; }
                  #printable-audit-report { position: absolute; left: 0; top: 0; width: 100%; padding: 0 !important; }
                  .MuiDialogActions-root { display: none !important; }
                  .MuiDialogTitle-root { display: none !important; }
                  .MuiDialog-paper { box-shadow: none !important; margin: 0 !important; width: 100% !important; max-width: none !important; max-height: none !important; }
                  /* Force all text in the report to be fully opaque black */
                  #printable-audit-report td,
                  #printable-audit-report th,
                  #printable-audit-report p,
                  #printable-audit-report span,
                  #printable-audit-report .MuiTypography-root,
                  #printable-audit-report .MuiTableCell-root {
                    color: #000 !important;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                  }
                }
                `}
              </style>
              
              <Box id="printable-audit-report">
                {/* Formal Header */}
                <Box sx={{ textAlign: 'center', borderBottom: '2px solid #000', pb: 2, mb: 3 }}>
                  <Typography variant="h5" fontWeight="bold" textTransform="uppercase">
                    Term Billing Audit Report
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {schoolInfo?.school?.name || 'School Name'}
                  </Typography>
                  <Typography variant="subtitle2">
                    Reporting Date: {new Date(auditReportDateFilter).toLocaleDateString()}
                  </Typography>
                  <Typography variant="subtitle2">
                    Term: {term || 'N/A'} | Academic Year: {academicYear || 'N/A'}
                  </Typography>
                </Box>

                {auditReportData.dailyTotals?.length > 0 && (
                  <Paper elevation={0} sx={{ border: '1px solid #000', borderRadius: 0, mb: 4, pageBreakInside: 'avoid' }}>
                    <DataTable><Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                          <TableCell colSpan={3} align="center" sx={{ borderBottom: '1px solid #000', fontWeight: 'bold' }}>
                            DAILY COLLECTION SUMMARY
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '40%' }}>Payment Date</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '25%' }}>Transactions</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '35%' }}>Total Collected (GH₵)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {auditReportData.dailyTotals.map((dayTotal) => (
                          <TableRow key={dayTotal.date}>
                            <TableCell>{new Date(`${dayTotal.date}T00:00:00`).toLocaleDateString()}</TableCell>
                            <TableCell align="center">{dayTotal.paidCount}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{dayTotal.totalAmount?.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: '#f7f7f7' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>TERM TOTAL</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                            {auditReportData.dailyTotals.reduce((sum, dayTotal) => sum + (Number(dayTotal.paidCount) || 0), 0)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {(auditReportData.termGrandTotal ?? auditReportData.dailyTotals.reduce((sum, dayTotal) => sum + (Number(dayTotal.totalAmount) || 0), 0)).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table></DataTable>
                  </Paper>
                )}

                {/* Master Summary */}
                <Paper elevation={0} sx={{ border: '1px solid #000', borderRadius: 0, mb: 4 }}>
                  <DataTable><Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                        <TableCell colSpan={2} align="center" sx={{ borderBottom: '1px solid #000', fontWeight: 'bold' }}>
                          EXECUTIVE SUMMARY
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #ccc', width: '50%' }}>Total Transactions (Payments)</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', width: '50%' }}>Grand Total Collected (GH₵)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" sx={{ borderRight: '1px solid #ccc' }}>{auditReportData.totalPaid}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                          {auditReportData.grandTotal?.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table></DataTable>
                </Paper>

                {/* Detailed Ledgers By Class */}
                {auditReportData.report.length === 0 ? (
                  <Typography textAlign="center" color="text.secondary" p={3}>
                    No payments collected on this date.
                  </Typography>
                ) : (
                  auditReportData.report.map((clsReport) => (
                    <Paper key={clsReport.classId} elevation={0} sx={{ border: '1px solid #000', borderRadius: 0, mb: 3, pageBreakInside: 'avoid' }}>
                      <DataTable><Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                            <TableCell colSpan={2} sx={{ fontWeight: 'bold', borderBottom: '1px solid #000' }}>
                              CLASS: {clsReport.className}
                            </TableCell>
                            <TableCell align="right" colSpan={2} sx={{ fontWeight: 'bold', borderBottom: '1px solid #000' }}>
                              Class Collection: GH₵{clsReport.totalAmount?.toFixed(2)} | Transactions: {clsReport.paidCount}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '40%' }}>Student Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '20%' }}>Payment Method</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '20%' }}>Time</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '20%' }}>Amount (GH₵)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {clsReport.students.map((student) => (
                            <TableRow key={student.studentId} sx={{ '& td': { borderBottom: '1px solid #eee' } }}>
                              <TableCell>{student.studentName}</TableCell>
                              <TableCell>{student.method}</TableCell>
                              <TableCell>{student.time}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{student.amount?.toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table></DataTable>
                    </Paper>
                  ))
                )}              </Box>
            </>
          ) : (
            <Alert severity="warning">Failed to load audit report data.</Alert>
          )}
        </DialogContent>
        <DialogActions className="no-print">
          <Button onClick={() => setShowAuditDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Reverse Payment Confirmation Dialog */}
      <Dialog
        open={reverseDialog.open}
        onClose={() => !reverseDialog.loading && setReverseDialog({ open: false, payment: null, bill: null, loading: false })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', color: 'error.main', pb: 1 }}>
          <Delete sx={{ mr: 1 }} /> Reverse Payment
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography variant="body1">
            Are you sure you want to reverse the payment of <strong style={{ color: '#d32f2f' }}>₵{reverseDialog.payment?.amount?.toFixed(2)}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This action cannot be undone. The amount will be deducted from the student's Total Paid and their balances will be updated automatically.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setReverseDialog({ open: false, payment: null, bill: null, loading: false })}
            disabled={reverseDialog.loading}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmReversePayment}
            color="error"
            variant="contained"
            disabled={reverseDialog.loading}
            startIcon={reverseDialog.loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {reverseDialog.loading ? 'Reversing...' : 'Reverse Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container >
  );
};

export default TermBillingPage;
